/**
 * The action row of a react-admin form page: delete (when the record can be
 * deleted) at the left, Cancel and Save at the right. It stays in view at the
 * foot of the page's scroll while the fields scroll under it, and sits in the
 * flow, so it never covers the last field; on a phone it clears the home
 * indicator.
 */

import React from 'react';
import { DeleteButton, SaveButton, Toolbar, ToolbarProps, useRedirect, useTranslate } from 'react-admin';
import { Box, Button } from '@mui/material';

export interface FormToolbarProps extends ToolbarProps {
  /** Where Cancel (and a completed delete) go back to — the list the record came from. */
  backTo: string;
  /** Show the delete action (an edit page of a record that can be deleted). */
  showDelete?: boolean;
  /** Replaces react-admin's delete (e.g. a custom endpoint). */
  onDelete?: () => void;
  /** Keep Save enabled while the form is pristine (state held outside the form). */
  alwaysEnableSave?: boolean;
}

export const FormToolbar: React.FC<FormToolbarProps> = ({
  backTo,
  showDelete = false,
  onDelete,
  alwaysEnableSave = false,
  ...rest
}) => {
  const translate = useTranslate();
  const redirect = useRedirect();

  return (
    <Toolbar
      {...rest}
      data-sticky-form-actions=""
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        px: { xs: 2, sm: 3 },
        pt: 1.5,
        pb: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 1.5 },
      }}
    >
      {showDelete &&
        (onDelete ? (
          <Button variant="outlined" color="error" onClick={onDelete}>
            {translate('ra.action.delete')}
          </Button>
        ) : (
          <DeleteButton variant="outlined" confirmColor="warning" redirect={backTo} />
        ))}
      <Box sx={{ flex: 1 }} />
      <Button variant="outlined" onClick={() => redirect(backTo)}>
        {translate('ra.action.cancel')}
      </Button>
      <SaveButton alwaysEnable={alwaysEnableSave} />
    </Toolbar>
  );
};

export default FormToolbar;
